import { useEffect, useRef, useId } from 'react'
import './confirmModal.css'

// Stroke icons in the Lucide style, decorative only: the title carries the meaning.
const ICONS = {
  warning: <><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" /><path d="M12 9v4" /><path d="M12 17h.01" /></>,
  info: <><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></>,
}

// Asks before an action that is hard to undo. A real dialog: named, focus moved in and trapped, Escape closes, focus returns to the trigger.
const ConfirmModal = ({ isOpen, title, message, confirmLabel, cancelLabel, onConfirm, onClose, loading, danger = true }) => {
  const dialogRef = useRef(null)
  const cancelRef = useRef(null)
  const id = useId()

  useEffect(() => {
    if (!isOpen) return
    const trigger = document.activeElement
    cancelRef.current?.focus() // the safe choice gets focus, so Enter never confirms by accident
    return () => trigger?.focus?.()
  }, [isOpen])

  if (!isOpen) return null

  const onKeyDown = (e) => {
    if (e.key === 'Escape' && !loading) return onClose()
    if (e.key !== 'Tab') return
    const focusable = [...dialogRef.current.querySelectorAll('button:not([disabled])')]
    if (!focusable.length) return e.preventDefault()
    const [first, last] = [focusable[0], focusable[focusable.length - 1]]
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
  }

  return (
    <div className='confirm_modal_overlay' onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) onClose() }}>
      <div ref={dialogRef} className='confirm_modal' role='alertdialog' aria-modal='true' aria-labelledby={`${id}-title`} aria-describedby={`${id}-message`} onKeyDown={onKeyDown}>
        <div className={`confirm_modal_icon ${danger ? 'confirm_modal_icon_danger' : ''}`}>
          <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            {ICONS[danger ? 'warning' : 'info']}
          </svg>
        </div>
        <div className='confirm_modal_body'>
          <h2 id={`${id}-title`}>{title}</h2>
          <p id={`${id}-message`}>{message}</p>
          <div className='confirm_modal_actions'>
            <button ref={cancelRef} className='confirm_modal_button confirm_modal_cancel' disabled={loading} onClick={onClose}>{cancelLabel}</button>
            <button className={`confirm_modal_button confirm_modal_confirm ${danger ? 'confirm_modal_confirm_danger' : ''}`} disabled={loading} aria-busy={loading || undefined} onClick={onConfirm}>
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConfirmModal
